import type { Mutation, Query } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { buildBaseErrorLogPayload, logError } from '../errorLogger';

/**
 * The axios interceptor throws "Redirecting to …" sentinels (access denied, not found,
 * error page) AFTER it has already logged the real API failure and navigated the user.
 * When that sentinel surfaces through a queryFn/mutationFn it isn't a new failure —
 * logging it again only added a second, less useful entry for the same root cause.
 */
function isNavigationSentinel(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith('Redirecting');
}

/**
 * Global QueryCache onError handler (wired in src/index.tsx as `queryCache: new
 * QueryCache({ onError: reportNonAxiosQueryError })`). Axios failures are already
 * logged with full request/response context by the axios response interceptor
 * (axiosConfig.ts) — this only covers what that interceptor never sees: a thrown bug
 * inside a queryFn/select transform that isn't a network failure at all (e.g. a bad
 * assumption about response shape — including react-query's own "data is undefined"
 * guard when a queryFn resolves to undefined). Log-only, deliberately no redirect —
 * an isolated query failing shouldn't take over the whole app the way a truly
 * uncaught error does.
 *
 * `query` is react-query's own `Query` instance for the one that failed — its
 * `queryKey` is recorded on the payload (see `ErrorLogPayload.queryKey`) so this can
 * be traced back to the exact query. react-query's "data is undefined" message
 * already embeds the key as text, but every other queryFn bug does not, and the
 * stack trace alone only ever points into react-query's own minified internals —
 * never the queryFn or component that actually caused it.
 */
export function reportNonAxiosQueryError(
  error: unknown,
  query?: Query<unknown, unknown, unknown, ReadonlyArray<unknown>>
): void {
  if (error instanceof AxiosError || isNavigationSentinel(error)) {
    return;
  }

  const normalizedError = error instanceof Error ? error : new Error(String(error));
  const payload = buildBaseErrorLogPayload('query-error', 'error', normalizedError.message, normalizedError.stack);
  if (query) {
    payload.queryKey = JSON.stringify(query.queryKey);
  }
  void logError(payload);
}

/**
 * Same purpose as `reportNonAxiosQueryError` above, but wired as MutationCache's
 * onError instead (`mutationCache: new MutationCache({ onError:
 * reportNonAxiosMutationError })` in src/index.tsx). MutationCache calls its onError
 * with `(error, variables, context, mutation)` — a different shape than QueryCache's
 * `(error, query)` — so this is a separate function rather than overloading the one
 * above. Most mutations in this app don't set an explicit `mutationKey`, in which
 * case `queryKey` is simply omitted on the payload (no worse than before this change).
 */
export function reportNonAxiosMutationError(
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation?: Mutation<unknown, unknown, unknown, unknown>
): void {
  if (error instanceof AxiosError || isNavigationSentinel(error)) {
    return;
  }

  const normalizedError = error instanceof Error ? error : new Error(String(error));
  const payload = buildBaseErrorLogPayload('query-error', 'error', normalizedError.message, normalizedError.stack);
  const mutationKey = mutation?.options.mutationKey;
  if (mutationKey) {
    payload.queryKey = JSON.stringify(mutationKey);
  }
  void logError(payload);
}

/**
 * Same skip-if-already-logged-by-axios rule as reportNonAxiosQueryError above,
 * for manual try/catch blocks that mix an axios call with non-axios follow-up
 * logic (e.g. fetch a comment, then map + print it) — a failure in the axios
 * call is already logged by the interceptor; a bug in the non-axios part
 * (the mapper, the print/copy utility, persisting the response, ...) is not,
 * unless this reports it. `context` is a short label identifying which action
 * failed (e.g. "Print alert from listing row"), prefixed onto the logged message
 * since these catches don't have a queryFn name to fall back on.
 */
export function reportNonAxiosUiError(error: unknown, context: string): void {
  if (error instanceof AxiosError || isNavigationSentinel(error)) {
    return;
  }

  const normalizedError = error instanceof Error ? error : new Error(String(error));
  const payload = buildBaseErrorLogPayload(
    'ui-action',
    'error',
    `${context}: ${normalizedError.message}`,
    normalizedError.stack
  );
  void logError(payload);
}
